import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { kalaSakthiService, freeKundliService } from '../../services/api';
import ChartContainer from '../vedic/results/ChartContainer';
import ErrorAlert from '../vedic/feedback/ErrorAlert';
import LoadingState from '../vedic/feedback/LoadingState';
import BirthDataForm from '../vedic/forms/BirthDataForm';
import { to24Hour } from '../vedic/forms/TimeInput';

/* --- Animations --- */
const fadeIn = keyframes`from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}`;
const pulse = keyframes`0%{transform:scale(1)}50%{transform:scale(1.05)}100%{transform:scale(1)}`;

/* --- Layout --- */
const Page = styled.div`max-width:1400px;margin:30px auto;padding:0 20px;`;
const Title = styled.h1`font-size:2rem;text-align:center;margin-bottom:5px;color:${p => p.theme.colors.text.primary};`;
const Sub = styled.p`text-align:center;color:${p => p.theme.colors.text.secondary};margin-bottom:25px;font-size:0.95rem;`;
const TopGrid = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:24px;@media(max-width:992px){grid-template-columns:1fr}`;
const Card = styled.div`background:${p => p.theme.colors.surface};border-radius:10px;box-shadow: ${p => p.theme.shadow.card};padding:24px;animation:${fadeIn} 0.3s ease;`;
const CardTitle = styled.h2`font-size:1.15rem;margin-bottom:16px;color:${p => p.theme.colors.text.primary};text-align:center;`;
const FormGroup = styled.div`margin-bottom:12px;`;
const Label = styled.label`display:block;margin-bottom:3px;font-weight:600;font-size:0.85rem;color:${p => p.theme.colors.text.secondary};`;
const Input = styled.input`width:100%;padding:9px 11px;border:1px solid ${p => p.theme.colors.border};border-radius:6px;font-size:0.9rem;box-sizing:border-box;&:focus{outline:none;border-color:#e91e63;box-shadow:0 0 0 2px rgba(233,30,99,0.15)}`;
const Select = styled.select`width:100%;padding:9px 11px;border:1px solid ${p => p.theme.colors.border};border-radius:6px;font-size:0.9rem;box-sizing:border-box;&:focus{outline:none;border-color:#e91e63}`;
const Row = styled.div`display:grid;grid-template-columns:${p => p.cols || '1fr 1fr'};gap:10px;@media(max-width:576px){grid-template-columns:1fr}`;
const Btn = styled.button`width:100%;padding:11px;background:linear-gradient(135deg,#e91e63,#9c27b0);color:${p => p.theme.colors.surface};border:none;border-radius:6px;font-size:1rem;font-weight:600;cursor:pointer;margin-top:8px;&:hover{opacity:0.9}&:disabled{opacity:0.5;cursor:not-allowed}`;
/* ErrBox replaced by shared ErrorAlert component */


/* --- Score Ring --- */
const RingWrap = styled.div`display:flex;flex-direction:column;align-items:center;margin:16px 0;`;
const Ring = styled.div`width:150px;height:150px;border-radius:50%;background:conic-gradient(${p => p.color} ${p => p.pct}%,${p => p.theme.colors.border} ${p => p.pct}%);display:flex;align-items:center;justify-content:center;animation:${pulse} 1s ease;`;
const RingInner = styled.div`width:120px;height:120px;border-radius:50%;background:${p => p.theme.colors.surface};display:flex;flex-direction:column;align-items:center;justify-content:center;`;
const ScoreNum = styled.span`font-size:2.4rem;font-weight:700;color:${p => p.color};`;
const ScoreBand = styled.span`font-size:0.8rem;font-weight:600;color:${p => p.theme.colors.text.secondary};text-transform:uppercase;letter-spacing:1px;`;

/* --- House Grid --- */
const HouseGrid = styled.div`display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-top:20px;@media(max-width:768px){grid-template-columns:repeat(3,1fr)}@media(max-width:480px){grid-template-columns:repeat(2,1fr)}`;
const HouseCard = styled.div`background:${p => p.expanded ? '#fce4ec' : p.theme.colors.background};border:1px solid ${p => p.expanded ? '#e91e63' : p.theme.colors.border};border-radius: ${p => p.theme.radius.md}px;padding:12px;cursor:pointer;transition:all 0.2s;&:hover{border-color:#e91e63;box-shadow:0 2px 8px rgba(233,30,99,0.15)}`;
const HouseNum = styled.div`font-weight:700;font-size:1.1rem;color:${p => p.theme.colors.text.primary};`;
const HouseInfo = styled.div`font-size:0.75rem;color:#777;margin:2px 0;`;
const ScoreBar = styled.div`height:6px;border-radius:3px;background:${p => p.theme.colors.border};margin:6px 0;overflow:hidden;`;
const ScoreFill = styled.div`height:100%;border-radius:3px;background:${p => p.color};width:${p => p.pct}%;transition:width 0.3s;`;
const Badge = styled.span`display:inline-block;padding:2px 7px;border-radius:10px;font-size:0.7rem;font-weight:600;color:${p => p.theme.colors.surface};background:${p => p.bg};`;
const DrillDown = styled.div`margin-top:8px;padding-top:8px;border-top:1px dashed ${p => p.theme.colors.border};font-size:0.78rem;color:${p => p.theme.colors.text.secondary};`;
const DrillRow = styled.div`display:flex;justify-content:space-between;padding:2px 0;`;

/* --- Tables --- */
const Table = styled.table`width:100%;border-collapse:collapse;margin-top:12px;font-size:0.82rem;`;
const Th = styled.th`text-align:left;padding:6px 8px;border-bottom:2px solid ${p => p.theme.colors.border};color:${p => p.theme.colors.text.secondary};font-weight:600;`;
const Td = styled.td`padding:5px 8px;border-bottom:1px solid ${p => p.theme.colors.background};`;

/* --- Yoga/Dosha --- */
const PillList = styled.div`display:flex;flex-wrap:wrap;gap:6px;margin-top:8px;`;
const Pill = styled.span`display:inline-block;padding:4px 10px;border-radius: ${p => p.theme.radius.lg}px;font-size:0.75rem;font-weight:600;color:${p => p.theme.colors.surface};background:${p => p.bg || p.theme.colors.text.secondary};`;

/* --- Tabs --- */
const TabRow = styled.div`display:flex;justify-content:center;gap:8px;margin-bottom:20px;`;
const Tab = styled.button`padding:10px 24px;border-radius:24px;border:2px solid ${p => p.active ? '#e91e63' : p.theme.colors.border};background:${p => p.active ? 'linear-gradient(135deg,#e91e63,#9c27b0)' : p.theme.colors.surface};color:${p => p.active ? p.theme.colors.surface : p.theme.colors.text.secondary};cursor:pointer;font-weight:600;font-size:0.9rem;transition:all 0.2s;&:hover{border-color:#e91e63}`;

/* --- Timeline chart --- */
const ChartWrap = styled.div`overflow-x:auto;margin-top:16px;`;

/* --- Helpers --- */
const today = () => new Date().toISOString().slice(0, 10);
const formatDurationParts = (dp) => {
  if (!dp) return '0';
  const p = [];
  if (dp.years) p.push(`${dp.years} Y`);
  if (dp.months) p.push(`${dp.months} M`);
  if (dp.days) p.push(`${dp.days} D`);
  return p.join(' ') || '0';
};

/* ========== RADAR SVG ========== */
const RadarWrap = styled.div`display:flex;justify-content:center;margin:10px 0;`;
const RadarChart = ({ data }) => {
  const dims = ['career', 'wealth', 'love', 'health', 'wisdom'];
  const labels = ['Career', 'Wealth', 'Love', 'Health', 'Wisdom'];
  const cx = 120, cy = 120, R = 90;
  const angles = dims.map((_, i) => (Math.PI * 2 * i) / dims.length - Math.PI / 2);
  const pt = (angle, r) => [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
  const gridLevels = [0.2, 0.4, 0.6, 0.8, 1.0];
  const points = dims.map((d, i) => { const val = Math.min((data[d] || 0) / 100, 1); return pt(angles[i], R * val); });
  const poly = points.map(p => p.join(',')).join(' ');
  return (
    <svg width={240} height={240} viewBox="0 0 240 240">
      {gridLevels.map(lv => <polygon key={lv} points={angles.map(a => pt(a, R * lv).join(',')).join(' ')} fill="none" stroke="#e0e0e0" strokeWidth={0.5} />)}
      {angles.map((a, i) => (
        <g key={i}>
          <line x1={cx} y1={cy} x2={pt(a, R)[0]} y2={pt(a, R)[1]} stroke="#e0e0e0" strokeWidth={0.5} />
          <text x={pt(a, R + 16)[0]} y={pt(a, R + 16)[1]} textAnchor="middle" dominantBaseline="middle" fontSize={10} fill="#555">{labels[i]}</text>
          <text x={pt(a, R * (Math.min((data[dims[i]] || 0) / 100, 1)) + 0.08)[0]} y={pt(a, R * (Math.min((data[dims[i]] || 0) / 100, 1)) + 0.08)[1] + 12} textAnchor="middle" fontSize={9} fontWeight={700} fill="#e91e63">{Math.round(data[dims[i]] || 0)}</text>
        </g>
      ))}
      <polygon points={poly} fill="rgba(233,30,99,0.18)" stroke="#e91e63" strokeWidth={2} />
      {points.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r={3} fill="#e91e63" />)}
    </svg>
  );
};

/* --- Descriptions --- */
const Desc = styled.p`font-size:0.8rem;color:${p => p.theme.colors.text.muted};line-height:1.5;margin:0 0 14px;text-align:center;`;
const SectionDesc = styled.p`font-size:0.78rem;color:${p => p.theme.colors.text.muted};line-height:1.4;margin:-8px 0 12px;`;

/* ========== DASHA INTERPRETATION PANEL ========== */
const InterpCard = styled.div`background:${p => p.theme.colors.surface};border-radius:10px;box-shadow: ${p => p.theme.shadow.card};padding:24px;margin-top:20px;animation:${fadeIn} 0.3s ease;`;
const InterpSection = styled.div`margin-bottom:16px;`;
const InterpLabel = styled.div`font-size:0.75rem;font-weight:700;color:#9c27b0;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;display:flex;align-items:center;gap:6px;cursor:pointer;user-select:none;`;
const InterpText = styled.div`font-size:0.82rem;color:#444;line-height:1.65;white-space:pre-wrap;`;
const DPIBar = styled.div`display:flex;align-items:center;gap:12px;margin:12px 0 18px;`;
const DPIFill = styled.div`flex:1;height:8px;border-radius: ${p => p.theme.radius.sm}px;background:${p => p.theme.colors.border};overflow:hidden;position:relative;&::after{content:'';position:absolute;left:0;top:0;height:100%;width:${p => Math.min(p.pct, 100)}%;background:${p => p.color};border-radius: ${p => p.theme.radius.sm}px;transition:width 0.5s ease;}`;
const DPILabel = styled.span`font-size:0.85rem;font-weight:700;color:${p => p.color};min-width:48px;`;
const InterpMeta = styled.div`display:flex;flex-wrap:wrap;gap:6px;margin-bottom:14px;`;

const LangToggle = styled.div`display:flex;justify-content:center;gap:4px;margin-bottom:14px;`;
const LangBtn = styled.button`padding:5px 16px;border-radius:16px;border:1.5px solid ${p => p.active ? '#9c27b0' : p.theme.colors.border};background:${p => p.active ? '#f3e5f5' : p.theme.colors.surface};color:${p => p.active ? '#7b1fa2' : p.theme.colors.text.muted};cursor:pointer;font-size:0.8rem;font-weight:600;transition:all 0.2s;&:hover{border-color:#9c27b0}`;

const KSDashaInterpretation = ({ interp, lang, onLangChange }) => {
  const [expanded, setExpanded] = useState({
    maha: true, antar: true, friendship: false, dignity: false, timing: false, remedial: false
  });
  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  if (!interp) return null;

  const isTa = lang === 'ta';
  const dpiColor = interp.dpi_color || '#999';
  const dpiPct = Math.round(interp.dpi * 100);
  const prof = interp.planet_profile;

  const planetLabel = isTa ? (prof?.name_ta || prof?.name_en || 'Mahadasha') : (prof?.name_en || 'Mahadasha');

  const sections = [
    { key: 'maha', icon: '🪐', label: isTa ? `${planetLabel} மகாதசை விளக்கம்` : `${planetLabel} Mahadasha Narrative`, text: interp.mahadasha_narrative },
    { key: 'antar', icon: '🔄', label: isTa ? `நடப்பு அந்தர்தசை: ${interp.antar_quality ? interp.antar_quality.replace(/_/g, ' ') : ''}` : `Current Antardasha: ${interp.antar_quality ? interp.antar_quality.replace(/_/g, ' ') : ''}`, text: interp.antardasha_narrative },
    { key: 'friendship', icon: '🤝', label: isTa ? 'மகாதசை-அந்தர்தசை உறவு' : 'MD-AD Relationship', text: interp.friendship_quality },
    { key: 'dignity', icon: '⚖️', label: isTa ? 'கிரக கௌரவம்' : 'Planetary Dignity', text: interp.dignity_note },
    { key: 'timing', icon: '⏱️', label: isTa ? 'கால வரையறை' : 'Timing Within Period', text: interp.timing_note },
  ];
  if (interp.remedial_advice) {
    sections.push({ key: 'remedial', icon: '💎', label: isTa ? 'பரிகாரங்கள்' : 'Remedial Guidance', text: interp.remedial_advice });
  }

  return (
    <InterpCard>
      <CardTitle>{isTa ? 'தசா விளக்கம்' : 'Dasha Interpretation'}</CardTitle>
      <Desc>{isTa ? 'உங்கள் நடப்பு விம்சோத்தரி தசா காலத்திற்கான தனிப்பயனாக்கப்பட்ட விளக்கம்.' : 'Personalized narrative for your current Vimshottari Dasha period, based on chart-specific planetary strength, dignity, and house activation.'}</Desc>

      <LangToggle>
        <LangBtn active={lang === 'en'} onClick={() => onLangChange('en')}>English</LangBtn>
        <LangBtn active={lang === 'ta'} onClick={() => onLangChange('ta')}>தமிழ்</LangBtn>
      </LangToggle>

      {/* DPI gauge */}
      <div style={{ textAlign: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: '0.78rem', color: '#888' }}>Dasha Power Index</span>
      </div>
      <DPIBar>
        <DPILabel color={dpiColor}>{dpiPct}%</DPILabel>
        <DPIFill pct={dpiPct} color={dpiColor} />
        <Badge bg={dpiColor} style={{ fontSize: '0.72rem' }}>{interp.dpi_label}</Badge>
      </DPIBar>

      {/* Meta pills */}
      <InterpMeta>
        {prof && <Pill bg="#7b1fa2">{prof.name_en} ({prof.name_ta})</Pill>}
        {interp.is_swabhukti && <Pill bg="#e91e63">Swabhukti</Pill>}
        {interp.is_chidra && <Pill bg="#d32f2f">Chidra Period</Pill>}
        {interp.antar_quality && <Pill bg="#1565c0">{interp.antar_quality.replace(/_/g, ' ')}</Pill>}
      </InterpMeta>

      {/* Karaka & Nature summary */}
      {prof && (
        <div style={{ fontSize: '0.78rem', color: '#666', marginBottom: 16, padding: '10px 14px', background: '#fafafa', borderRadius: 8, lineHeight: 1.5 }}>
          <strong style={{ color: '#333' }}>{isTa ? 'கருப்பொருள்:' : 'Theme:'}</strong> {isTa ? (prof.theme_ta || prof.theme_en) : prof.theme_en}<br />
          <strong style={{ color: '#333' }}>{isTa ? 'இயல்பு:' : 'Nature:'}</strong> {prof.nature_en}<br />
          <strong style={{ color: '#333' }}>{isTa ? 'காரகத்துவம்:' : 'Karaka for:'}</strong> {prof.karaka_for?.join(', ')}
        </div>
      )}

      {/* Narrative sections */}
      {sections.map(s => s.text ? (
        <InterpSection key={s.key}>
          <InterpLabel onClick={() => toggle(s.key)}>
            <span>{s.icon}</span>
            <span style={{ flex: 1 }}>{s.label}</span>
            <span style={{ fontSize: '0.85rem', color: '#bbb', transform: expanded[s.key] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
          </InterpLabel>
          {expanded[s.key] && (
            <InterpText>
              {s.text.split('\n\n').map((para, i) => (
                <p key={i} style={{ margin: '0 0 10px' }}>{para}</p>
              ))}
            </InterpText>
          )}
        </InterpSection>
      ) : null)}

      {/* Remedial summary box (always visible if present) */}
      {prof?.remedial && (
        <div style={{ marginTop: 8, padding: '12px 14px', background: '#fff8e1', borderRadius: 8, border: '1px solid #ffe082', fontSize: '0.78rem', lineHeight: 1.5 }}>
          <div style={{ fontWeight: 700, color: '#f57f17', marginBottom: 6 }}>Quick Remedial Reference</div>
          <div><strong>Gemstone:</strong> {prof.remedial.gemstone}</div>
          <div><strong>Temple:</strong> {prof.remedial.temple}</div>
          <div><strong>Deity:</strong> {prof.remedial.deity}</div>
          <div><strong>Fasting:</strong> {prof.remedial.fasting}</div>
        </div>
      )}
    </InterpCard>
  );
};

/* ========== VIMSHOTTARI DASHA COMPONENT (KalaSakthi-specific) ========== */
const KSDashaSection = ({ dasha, dashaActivation, ks, houseAnalysis }) => {
  const [selectedMD, setSelectedMD] = useState(null); // mahadasha index for bukthi drill-down
  const [bukthiPopup, setBukthiPopup] = useState(null); // { items, breadcrumb, level }


  // Compute houses a planet activates: houses it RULES + house it OCCUPIES
  const SHADOW = ['Rahu', 'Ketu'];
  const houseActivation = (lord) => {
    if (SHADOW.includes(lord)) return null; // shadow planets don't own signs
    if (!houseAnalysis) return null;
    const houses = new Set();
    // Houses this planet rules (is lord of)
    for (const h of houseAnalysis) {
      if (h.lord === lord) houses.add(h.house);
    }
    // House this planet occupies
    for (const h of houseAnalysis) {
      if (h.occupants?.includes(lord)) houses.add(h.house);
    }
    return houses.size > 0 ? [...houses].sort((a, b) => a - b) : null;
  };

  const pssFor = (lord) => {
    const p = ks.pss?.find(x => x.planet === lord);
    return p ? p.pss : 0;
  };

  return (
    <Card style={{ marginTop: 20 }}>
      <CardTitle>Vimshottari Dasha</CardTitle>
      <SectionDesc>The planetary period system that times life events. Click a Mahadasha to see sub-periods. House activation shows which life areas each lord energizes.</SectionDesc>

      {/* Currently Running */}
      <div style={{background:'linear-gradient(135deg,#f3e5f5,#e8eaf6)',borderRadius:10,padding:14,marginBottom:16}}>
        <div style={{fontSize:'0.75rem',color:'#7b1fa2',fontWeight:600,marginBottom:6}}>Currently Running</div>
        <div style={{fontSize:'0.9rem',fontWeight:600,lineHeight:1.6}}>
          <span style={{color:'#7b1fa2'}}>{dasha.current_dasha.mahadasha}</span> Mahadasha &rarr;{' '}
          <span style={{color:'#1565c0'}}>{dasha.current_dasha.antardasha}</span> Antardasha &rarr;{' '}
          <span style={{color:'#2e7d32'}}>{dasha.current_dasha.pratyantdasha}</span> Pratyantdasha
          {dasha.current_dasha.sukshuma && <> &rarr; <span style={{color:'#e65100'}}>{dasha.current_dasha.sukshuma}</span> Sukshuma</>}
          {dasha.current_dasha.prana && <> &rarr; <span style={{color:'#880e4f'}}>{dasha.current_dasha.prana}</span> Prana</>}
        </div>
      </div>

      {/* House Activation for current dasha/antar */}
      {dashaActivation && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16}}>
          {[
            { lord: dashaActivation.dasha_lord, houses: dashaActivation.dasha_houses, pss: dashaActivation.dasha_lord_pss, period: dashaActivation.dasha_period, label: 'Mahadasha', bg: '#f3e5f5', color: '#7b1fa2' },
            { lord: dashaActivation.antar_lord, houses: dashaActivation.antar_houses, pss: dashaActivation.antar_lord_pss, period: dashaActivation.antar_period, label: 'Antardasha', bg: '#e3f2fd', color: '#1565c0' },
          ].map(({ lord, houses, pss, period, label, bg, color }) => (
            <div key={label} style={{background:bg,borderRadius:8,padding:12}}>
              <div style={{fontWeight:700,color,marginBottom:4,fontSize:'0.9rem'}}>{lord} {label}</div>
              {period && <div style={{fontSize:'0.7rem',color:'#888',marginBottom:6}}>{period}</div>}
              <div style={{fontSize:'0.82rem'}}>
                {(houses||[]).length > 0
                  ? <>Activates: {houses.map(h => { const house = ks.houses?.find(x => x.house === h); return <Badge key={h} bg={house?.color || '#ccc'} style={{margin:'0 2px'}}>H{h} ({house?.score_out_100||'?'})</Badge>; })}</>
                  : <span style={{fontSize:'0.75rem',color:'#888',fontStyle:'italic'}}>Shadow planet — activates by occupation & aspect</span>
                }
              </div>
              {pss > 0 && <div style={{fontSize:'0.75rem',marginTop:4}}>PSS: <strong style={{color: pss >= 0.3 ? '#4caf50' : '#f44336'}}>{pss.toFixed(3)}</strong></div>}
            </div>
          ))}
        </div>
      )}

      {/* Birth Dasha Balance */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:16}}>
        <div style={{background:'#fafafa',borderRadius:8,padding:10,textAlign:'center'}}>
          <div style={{fontSize:'0.7rem',color:'#888'}}>Birth Dasha Lord</div>
          <div style={{fontWeight:700,fontSize:'1rem'}}>{dasha.dasha_balance.lord}</div>
        </div>
        <div style={{background:'#fafafa',borderRadius:8,padding:10,textAlign:'center'}}>
          <div style={{fontSize:'0.7rem',color:'#888'}}>Balance at Birth</div>
          <div style={{fontWeight:700,fontSize:'1rem'}}>{formatDurationParts(dasha.dasha_balance.remaining_parts)}</div>
        </div>
        <div style={{background:'#fafafa',borderRadius:8,padding:10,textAlign:'center'}}>
          <div style={{fontSize:'0.7rem',color:'#888'}}>Moon Nakshatra</div>
          <div style={{fontWeight:700,fontSize:'1rem'}}>{dasha.moon_nakshatra}</div>
        </div>
      </div>

      {/* Mahadasha Periods */}
      <div style={{fontSize:'0.95rem',fontWeight:600,marginBottom:8}}>Mahadasha Periods</div>
      <Table>
        <thead><tr><Th>Lord</Th><Th>Duration</Th><Th>Start</Th><Th>End</Th><Th>Houses</Th><Th>PSS</Th></tr></thead>
        <tbody>
          {dasha.mahadashas.map((md, i) => {
            const houses = houseActivation(md.lord);
            const pss = pssFor(md.lord);
            return (
              <tr key={i} onClick={() => setSelectedMD(selectedMD === i ? null : i)}
                style={{cursor:'pointer',background: md.is_current ? '#f3e5f5' : selectedMD === i ? '#f5f5f5' : 'transparent',fontWeight: md.is_current ? 700 : 400}}>
                <Td style={{fontWeight:700}}>{md.lord} {md.is_current && <span style={{fontSize:'0.6rem',background:'#7b1fa2',color:'#fff',padding:'1px 5px',borderRadius:8,marginLeft:4}}>NOW</span>}</Td>
                <Td>{formatDurationParts(md.duration_parts)}</Td>
                <Td style={{fontSize:'0.78rem'}}>{md.start_date}</Td>
                <Td style={{fontSize:'0.78rem'}}>{md.end_date}</Td>
                <Td>{houses ? houses.map(h => `H${h}`).join(', ') : <span style={{color:'#ccc',fontSize:'0.75rem'}}>shadow</span>}</Td>
                <Td style={{color: pss >= 0.3 ? '#4caf50' : pss > 0 ? '#f44336' : '#ccc'}}>{pss > 0 ? pss.toFixed(3) : '—'}</Td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      <div style={{fontSize:'0.72rem',color:'#aaa',textAlign:'center',marginTop:6}}>Click a Mahadasha to see Bukthi periods</div>

      {/* Bukthi Drill-down (inline expand) */}
      {selectedMD !== null && dasha.mahadashas[selectedMD]?.antardashas && (
        <div style={{background:'#fafafa',borderRadius:10,padding:14,marginTop:12,border:'1px solid #eee'}}>
          <div style={{fontWeight:700,fontSize:'0.9rem',marginBottom:8,color:'#7b1fa2'}}>
            {dasha.mahadashas[selectedMD].lord} Mahadasha — Bukthi Periods
          </div>
          <Table>
            <thead><tr><Th>Bukthi Lord</Th><Th>Start</Th><Th>End</Th><Th>Houses</Th><Th>PSS</Th><Th></Th></tr></thead>
            <tbody>
              {dasha.mahadashas[selectedMD].antardashas.map((ad, j) => {
                const adHouses = houseActivation(ad.lord);
                const adPss = pssFor(ad.lord);
                return (
                  <tr key={j}
                    onClick={() => ad.pratyantdashas?.length > 0 && setBukthiPopup({ items: ad.pratyantdashas, breadcrumb: [dasha.mahadashas[selectedMD].lord, ad.lord], level: 'anthara' })}
                    style={{cursor: ad.pratyantdashas?.length > 0 ? 'pointer' : 'default', background: ad.is_current ? '#e8eaf6' : 'transparent', fontWeight: ad.is_current ? 600 : 400}}>
                    <Td style={{fontWeight:600}}>{ad.lord} {ad.is_current && <span style={{fontSize:'0.6rem',background:'#1565c0',color:'#fff',padding:'1px 5px',borderRadius:8,marginLeft:4}}>NOW</span>}</Td>
                    <Td style={{fontSize:'0.78rem'}}>{ad.start_date}</Td>
                    <Td style={{fontSize:'0.78rem'}}>{ad.end_date}</Td>
                    <Td>{adHouses ? adHouses.map(h => `H${h}`).join(', ') : <span style={{color:'#ccc',fontSize:'0.7rem'}}>—</span>}</Td>
                    <Td style={{color: adPss >= 0.3 ? '#4caf50' : adPss > 0 ? '#f44336' : '#ccc',fontSize:'0.8rem'}}>{adPss > 0 ? adPss.toFixed(3) : '—'}</Td>
                    <Td>{ad.pratyantdashas?.length > 0 && <span style={{fontSize:'0.65rem',color:'#999'}}>▸</span>}</Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <div style={{fontSize:'0.72rem',color:'#aaa',textAlign:'center',marginTop:4}}>Click a Bukthi to see Anthara periods</div>
        </div>
      )}

      {/* Anthara/Sukshuma/Prana Popup */}
      {bukthiPopup && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.4)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={() => setBukthiPopup(null)}>
          <div style={{background:'#fff',borderRadius:14,padding:20,maxWidth:520,width:'90%',maxHeight:'80vh',overflow:'auto',boxShadow:'0 8px 32px rgba(0,0,0,0.2)'}} onClick={e => e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
              <div>
                <div style={{fontWeight:700,fontSize:'1rem'}}>
                  {{anthara:'Anthara',sukshuma:'Sukshuma',prana:'Prana'}[bukthiPopup.level]} Periods
                </div>
                <div style={{fontSize:'0.75rem',color:'#888'}}>{bukthiPopup.breadcrumb.join(' → ')}</div>
              </div>
              <button onClick={() => setBukthiPopup(null)} style={{background:'none',border:'none',fontSize:'1.5rem',cursor:'pointer',color:'#888'}}>&times;</button>
            </div>
            <Table>
              <thead><tr><Th>Lord</Th><Th>Start</Th><Th>End</Th><Th></Th></tr></thead>
              <tbody>
                {bukthiPopup.items.map((item, i) => (
                  <tr key={i}
                    onClick={() => {
                      if (bukthiPopup.level === 'anthara' && item.sukshumas?.length > 0)
                        setBukthiPopup({ items: item.sukshumas, breadcrumb: [...bukthiPopup.breadcrumb, item.lord], level: 'sukshuma' });
                      else if (bukthiPopup.level === 'sukshuma' && item.pranas?.length > 0)
                        setBukthiPopup({ items: item.pranas, breadcrumb: [...bukthiPopup.breadcrumb, item.lord], level: 'prana' });
                    }}
                    style={{cursor: (bukthiPopup.level !== 'prana') ? 'pointer' : 'default', background: item.is_current ? '#e8eaf6' : 'transparent', fontWeight: item.is_current ? 600 : 400}}>
                    <Td style={{fontWeight:600}}>{item.lord} {item.is_current && <span style={{fontSize:'0.6rem',background:'#2e7d32',color:'#fff',padding:'1px 5px',borderRadius:8,marginLeft:4}}>NOW</span>}</Td>
                    <Td style={{fontSize:'0.78rem'}}>{item.start_date}</Td>
                    <Td style={{fontSize:'0.78rem'}}>{item.end_date}</Td>
                    <Td>{(bukthiPopup.level !== 'prana') && <span style={{fontSize:'0.65rem',color:'#999'}}>▸</span>}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {bukthiPopup.level !== 'prana' && (
              <div style={{fontSize:'0.72rem',color:'#aaa',textAlign:'center',marginTop:6}}>
                {bukthiPopup.level === 'anthara' && 'Click to see Sukshuma periods'}
                {bukthiPopup.level === 'sukshuma' && 'Click to see Prana periods'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Life Period Timeline (visual) */}
      <div style={{marginTop:16}}>
        <div style={{fontSize:'0.85rem',fontWeight:600,marginBottom:8}}>Life Period Timeline</div>
        <div style={{overflowX:'auto'}}>
          <svg width={Math.max(dasha.mahadashas.length * 90, 700)} height={60} style={{display:'block'}}>
            {dasha.mahadashas.map((md, i) => {
              const w = 80, gap = 8, x = i * (w + gap) + 4;
              return (
                <g key={i} style={{cursor:'pointer'}} onClick={() => setSelectedMD(selectedMD === i ? null : i)}>
                  <rect x={x} y={8} width={w} height={36} rx={6} fill={md.is_current ? '#e91e63' : selectedMD === i ? '#e0e0e0' : '#f5f5f5'} stroke={md.is_current ? '#c2185b' : '#ddd'} strokeWidth={1} />
                  <text x={x + w/2} y={24} textAnchor="middle" fontSize={11} fontWeight={md.is_current ? 700 : 400} fill={md.is_current ? '#fff' : '#555'}>{md.lord}</text>
                  <text x={x + w/2} y={38} textAnchor="middle" fontSize={7.5} fill={md.is_current ? '#ffcdd2' : '#aaa'}>{md.start_date?.slice(0,4)}–{md.end_date?.slice(0,4)}</text>
                </g>
              );
            })}
          </svg>
        </div>
      </div>
    </Card>
  );
};

/* --- Yoga stars helper --- */
const yogaStars = (eff) => {
  if (eff >= 1.05) return '★★★★★';
  if (eff >= 1.03) return '★★★★☆';
  if (eff >= 1.02) return '★★★☆☆';
  if (eff >= 1.01) return '★★☆☆☆';
  return '★☆☆☆☆';
};
const doshaStars = (eff) => {
  if (eff <= 0.85) return '▼▼▼▼▼';
  if (eff <= 0.90) return '▼▼▼▼';
  if (eff <= 0.93) return '▼▼▼';
  if (eff <= 0.96) return '▼▼';
  return '▼';
};

/* ========== LIFE FORECAST PANEL (own component for hooks) ========== */
const LifeForecastPanel = ({ result, expandedHouse, setExpandedHouse, birthPayload }) => {
  const allPeriods = result?.life_forecast?.periods;
  const [forecastFilter, setForecastFilter] = useState('all');
  const [popupPeriod, setPopupPeriod] = useState(null);
  const [popupInterp, setPopupInterp] = useState(null);
  const [popupLoading, setPopupLoading] = useState(false);

  if (!allPeriods?.length) return null;

  const openPeriodPopup = async (p) => {
    setPopupPeriod(p);
    setPopupInterp(null);
    setPopupLoading(true);
    try {
      const payload = { ...birthPayload(), override_md: p.md_lord, override_ad: p.ad_lord };
      const r = await kalaSakthiService.computeV2(payload);
      setPopupInterp(r.data.dasha_interpretation);
    } catch (err) { console.error(err); }
    setPopupLoading(false);
  };

  const filteredGrouped = {};
  allPeriods.forEach(p => {
    if (forecastFilter !== 'all' && p.category !== forecastFilter) return;
    if (!filteredGrouped[p.md_lord]) filteredGrouped[p.md_lord] = [];
    filteredGrouped[p.md_lord].push(p);
  });
  const mdOrder = [...new Set(allPeriods.map(p => p.md_lord))].filter(md => filteredGrouped[md]?.length > 0);

  return (
    <>
    <Card style={{ marginTop: 20 }}>
      <CardTitle>Life Period Forecast</CardTitle>
      <SectionDesc>Every Mahadasha-Antardasha period scored 0-100. Click any period to see full dasha interpretation.</SectionDesc>

      {/* Filter buttons */}
      <div style={{display:'flex',gap:8,marginBottom:14,flexWrap:'wrap'}}>
        {[['all','All',null],['good','Good','#4caf50'],['neutral','Neutral','#ff9800'],['challenging','Challenging','#f44336']].map(([val,label,col]) => (
          <button key={val} onClick={() => setForecastFilter(val)}
            style={{padding:'4px 14px',borderRadius:16,border:`1.5px solid ${forecastFilter===val ? (col||'#333') : '#ddd'}`,
              background:forecastFilter===val ? (col ? col+'18' : '#f5f5f5') : '#fff',
              color:forecastFilter===val ? (col||'#333') : '#888',fontWeight:forecastFilter===val?700:400,
              fontSize:'0.78rem',cursor:'pointer'}}>{label} ({val==='all' ? allPeriods.length : allPeriods.filter(p=>p.category===val).length})</button>
        ))}
      </div>

      {/* Life pattern */}
      {result.life_forecast.life_pattern && (
        <div style={{background:'linear-gradient(135deg,#e3f2fd,#f3e5f5)',borderRadius:10,padding:14,marginBottom:16}}>
          <div style={{fontSize:'0.8rem',fontWeight:700,color:'#1565c0',marginBottom:4}}>Life Pattern: {result.life_forecast.life_pattern}</div>
          {result.life_forecast.peak_age_range && <div style={{fontSize:'0.75rem',color:'#7b1fa2'}}>Peak: {result.life_forecast.peak_age_range}</div>}
          {result.life_forecast.best_period && <div style={{fontSize:'0.72rem',color:'#2e7d32',marginTop:4}}>Best: {result.life_forecast.best_period.period} ({result.life_forecast.best_period.start_date?.slice(0,4)}-{result.life_forecast.best_period.end_date?.slice(0,4)}) — {result.life_forecast.best_period.score}/100</div>}
          {result.life_forecast.worst_period && <div style={{fontSize:'0.72rem',color:'#c62828'}}>Hardest: {result.life_forecast.worst_period.period} ({result.life_forecast.worst_period.start_date?.slice(0,4)}-{result.life_forecast.worst_period.end_date?.slice(0,4)}) — {result.life_forecast.worst_period.score}/100</div>}
        </div>
      )}

      {/* Life Power Curve — FULL WIDTH */}
      <div style={{width:'100%',marginBottom:16}}>
        <svg width="100%" viewBox={`0 0 ${allPeriods.length * 12 + 20} 100`} preserveAspectRatio="none" style={{display:'block',height:140}}>
          {allPeriods.map((p, i) => {
            const x = i * 12 + 6;
            const barH = Math.max(p.score * 0.8, 2);
            const color = p.category === 'good' ? '#4caf50' : p.category === 'challenging' ? '#f44336' : '#ff9800';
            const filtered = forecastFilter !== 'all' && p.category !== forecastFilter;
            return (
              <g key={i} style={{cursor:'pointer'}} onClick={() => openPeriodPopup(p)}>
                <rect x={x} y={80-barH} width={10} height={barH} rx={2}
                  fill={p.is_current ? '#e91e63' : color}
                  opacity={filtered ? 0.15 : (p.is_current ? 1 : 0.75)} />
                {i % 9 === 0 && <text x={x+5} y={95} fontSize={6} fill="#999" textAnchor="middle">{p.start_date?.slice(0,4)}</text>}
                {p.is_current && <circle cx={x+5} cy={80-barH-4} r={2.5} fill="#e91e63" />}
              </g>
            );
          })}
          <line x1={0} y1={80-35*0.8} x2={allPeriods.length*12+20} y2={80-35*0.8} stroke="#ff9800" strokeDasharray="4,4" strokeWidth={0.5} />
          <line x1={0} y1={80-60*0.8} x2={allPeriods.length*12+20} y2={80-60*0.8} stroke="#4caf50" strokeDasharray="4,4" strokeWidth={0.5} />
          <text x={2} y={80-35*0.8-2} fontSize={5} fill="#ff9800">35</text>
          <text x={2} y={80-60*0.8-2} fontSize={5} fill="#4caf50">60</text>
        </svg>
      </div>

      {/* Period list — grouped by MD */}
      {mdOrder.map(md => {
        const periods = filteredGrouped[md];
        const allMD = allPeriods.filter(p => p.md_lord === md);
        const avgScore = Math.round(allMD.reduce((s,p) => s + p.score, 0) / allMD.length);
        const avgColor = avgScore >= 60 ? '#4caf50' : avgScore >= 35 ? '#ff9800' : '#f44336';
        const isExpMD = expandedHouse === 'forecast_' + md;
        return (
          <div key={md} style={{marginBottom:8}}>
            <div onClick={() => setExpandedHouse(isExpMD ? null : 'forecast_' + md)}
              style={{cursor:'pointer',display:'flex',justifyContent:'space-between',alignItems:'center',
                padding:'8px 12px',borderRadius:6,background: isExpMD ? '#fafafa' : '#f9f9f9',
                border:`1px solid ${isExpMD ? avgColor : '#eee'}`}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{fontWeight:700,fontSize:'0.9rem'}}>{md} Mahadasha</span>
                <span style={{fontSize:'0.7rem',color:'#888'}}>{allMD[0]?.start_date?.slice(0,4)}-{allMD[allMD.length-1]?.end_date?.slice(0,4)}</span>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{fontSize:'0.75rem',color:avgColor,fontWeight:700}}>avg {avgScore}</span>
                <span style={{fontSize:'0.7rem',color:'#aaa'}}>{isExpMD ? '▲' : '▼'}</span>
              </div>
            </div>
            {isExpMD && (
              <div style={{padding:'8px 0'}}>
                {periods.map((p, i) => {
                  const catColor = p.category === 'good' ? '#4caf50' : p.category === 'challenging' ? '#f44336' : '#ff9800';
                  return (
                    <div key={i} onClick={() => openPeriodPopup(p)}
                      style={{cursor:'pointer',display:'flex',alignItems:'center',gap:8,padding:'6px 12px',
                        borderRadius:4,marginBottom:2,background: p.is_current ? '#fce4ec' : 'transparent',
                        borderLeft:`3px solid ${catColor}`}}>
                      <span style={{color:catColor,fontSize:'0.8rem'}}>●</span>
                      <span style={{fontWeight:p.is_current ? 700 : 400,fontSize:'0.82rem',minWidth:100}}>{p.period}</span>
                      <span style={{fontSize:'0.7rem',color:'#888',minWidth:80}}>{p.start_date?.slice(0,7)} to {p.end_date?.slice(0,7)}</span>
                      <div style={{flex:1,height:6,background:'#f0f0f0',borderRadius:3,overflow:'hidden'}}>
                        <div style={{height:'100%',width:`${p.score}%`,background:catColor,borderRadius:3}} />
                      </div>
                      <span style={{fontWeight:700,fontSize:'0.8rem',color:catColor,minWidth:30,textAlign:'right'}}>{p.score}</span>
                      {p.is_current && <Badge bg="#e91e63" style={{fontSize:'0.55rem'}}>NOW</Badge>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </Card>

    {/* INTERPRETATION POPUP */}
    {popupPeriod && (
      <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}
        onClick={(e) => { if (e.target === e.currentTarget) { setPopupPeriod(null); setPopupInterp(null); } }}>
        <div style={{background:'#fff',borderRadius:12,maxWidth:700,width:'100%',maxHeight:'85vh',overflow:'auto',padding:24,position:'relative'}}>
          <button onClick={() => { setPopupPeriod(null); setPopupInterp(null); }}
            style={{position:'absolute',top:12,right:16,background:'none',border:'none',fontSize:'1.4rem',cursor:'pointer',color:'#999'}}>×</button>
          <div style={{marginBottom:16}}>
            <div style={{fontSize:'1.2rem',fontWeight:700,color:'#333'}}>{popupPeriod.period}</div>
            <div style={{fontSize:'0.8rem',color:'#888'}}>{popupPeriod.start_date} to {popupPeriod.end_date}</div>
            <div style={{display:'flex',gap:8,marginTop:8,flexWrap:'wrap'}}>
              <Badge bg={popupPeriod.category === 'good' ? '#4caf50' : popupPeriod.category === 'challenging' ? '#f44336' : '#ff9800'}>
                Score: {popupPeriod.score}/100 — {popupPeriod.category}
              </Badge>
              <Badge bg="#7986cb">{popupPeriod.friendship}</Badge>
              {popupPeriod.is_current && <Badge bg="#e91e63">CURRENT</Badge>}
            </div>
            {popupPeriod.summary && <div style={{fontSize:'0.82rem',color:'#555',marginTop:8,fontStyle:'italic'}}>{popupPeriod.summary}</div>}
            {popupPeriod.top_house && <div style={{fontSize:'0.78rem',color:'#1565c0',marginTop:4}}>Top activation: {popupPeriod.top_house} ({popupPeriod.top_house_pct}%)</div>}
            {popupPeriod.key_themes?.length > 0 && (
              <div style={{display:'flex',gap:4,marginTop:6,flexWrap:'wrap'}}>
                {popupPeriod.key_themes.map((t,i) => <span key={i} style={{fontSize:'0.68rem',background:'#e3f2fd',color:'#1565c0',padding:'2px 8px',borderRadius:10}}>{t}</span>)}
              </div>
            )}
          </div>
          {popupLoading && <div style={{textAlign:'center',padding:30,color:'#888'}}>Loading interpretation...</div>}
          {popupInterp && (
            <div style={{borderTop:'1px solid #eee',paddingTop:16}}>
              <div style={{fontSize:'0.85rem',fontWeight:700,color:'#333',marginBottom:8}}>Mahadasha Narrative</div>
              <div style={{fontSize:'0.8rem',color:'#444',lineHeight:1.7,whiteSpace:'pre-line',marginBottom:16}}>{popupInterp.mahadasha_narrative}</div>
              <div style={{fontSize:'0.85rem',fontWeight:700,color:'#333',marginBottom:8}}>Antardasha Interpretation</div>
              <div style={{fontSize:'0.8rem',color:'#444',lineHeight:1.7,whiteSpace:'pre-line',marginBottom:16}}>{popupInterp.antardasha_narrative}</div>
              {popupInterp.friendship_quality && <>
                <div style={{fontSize:'0.85rem',fontWeight:700,color:'#333',marginBottom:4}}>Friendship</div>
                <div style={{fontSize:'0.8rem',color:'#555',lineHeight:1.6,marginBottom:12}}>{popupInterp.friendship_quality}</div>
              </>}
              {popupInterp.dignity_note && <>
                <div style={{fontSize:'0.85rem',fontWeight:700,color:'#333',marginBottom:4}}>Dignity</div>
                <div style={{fontSize:'0.8rem',color:'#555',lineHeight:1.6,marginBottom:12}}>{popupInterp.dignity_note}</div>
              </>}
              {popupInterp.timing_note && <>
                <div style={{fontSize:'0.85rem',fontWeight:700,color:'#333',marginBottom:4}}>Timing</div>
                <div style={{fontSize:'0.8rem',color:'#555',lineHeight:1.6,marginBottom:12}}>{popupInterp.timing_note}</div>
              </>}
              {popupInterp.remedial_advice && <>
                <div style={{fontSize:'0.85rem',fontWeight:700,color:'#333',marginBottom:4}}>Remedial Measures</div>
                <div style={{fontSize:'0.8rem',color:'#555',lineHeight:1.6,whiteSpace:'pre-line',marginBottom:12}}>{popupInterp.remedial_advice}</div>
              </>}
              <div style={{display:'flex',gap:12,flexWrap:'wrap',marginTop:8,paddingTop:8,borderTop:'1px solid #eee'}}>
                <span style={{fontSize:'0.72rem',color:'#888'}}>DPI: <strong>{popupInterp.dpi?.toFixed(3)}</strong> ({popupInterp.dpi_label})</span>
                {popupInterp.is_swabhukti && <Badge bg="#7b1fa2" style={{fontSize:'0.6rem'}}>Swabhukti</Badge>}
                {popupInterp.is_chidra && <Badge bg="#c62828" style={{fontSize:'0.6rem'}}>Chidra</Badge>}
              </div>
            </div>
          )}
        </div>
      </div>
    )}
    </>
  );
};

/* ========== SHARED: HOUSE RESULTS ========== */
const HouseResults = ({ ks, result, expandedHouse, setExpandedHouse, interpLang, setInterpLang, reloadInterpretation, birthPayload }) => (
  <>
    {/* ---- RASHI CHART + PLANET-IN-HOUSE ---- */}
    {result?.chart && (<>
      {/* ---- D1 RASHI + D9 NAVAMSA ---- */}
      <TopGrid style={{ marginTop: 20 }}>
        <Card>
          <ChartContainer chartData={result.chart} title="Rashi Chart (D1)" />
        </Card>
        {result.navamsa ? (
          <Card>
            <SectionDesc>The dharma/marriage chart. Planets may shift signs — cross-reference with D1 for marriage, spiritual path, and inner nature analysis.</SectionDesc>
            <ChartContainer chartData={{
              ascendant: { ...result.chart.ascendant, sign_index: result.navamsa.ascendant?.sign_index ?? 0, sign_name: result.navamsa.ascendant?.sign_name ?? '' },
              planets: (result.navamsa.planets || []).map(vp => {
                const bp = result.chart.planets?.find(p => p.planet === vp.planet) || {};
                return { ...bp, sign_index: vp.sign_index ?? 0, sign_name: vp.sign_name ?? '', planet: vp.planet };
              })
            }} title="Navamsa Chart (D9)" isNavamsa />
          </Card>
        ) : (
          <Card>
            <CardTitle>Planet-in-House Map</CardTitle>
            <SectionDesc>Which planets occupy which houses, and where each house lord is placed.</SectionDesc>
            <Table>
              <thead><tr><Th>House</Th><Th>Sign</Th><Th>Occupants</Th><Th>Lord</Th><Th>Lord in</Th></tr></thead>
              <tbody>
                {(result.house_analysis || []).map(h => (
                  <tr key={h.house} style={h.occupants?.length > 0 ? {fontWeight:600} : {}}>
                    <Td>H{h.house}</Td><Td>{h.sign}</Td>
                    <Td>{h.occupants?.length > 0 ? h.occupants.join(', ') : <span style={{color:'#ccc'}}>—</span>}</Td>
                    <Td>{h.lord}</Td><Td>H{h.lord_house} ({h.lord_sign})</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        )}
      </TopGrid>

      {/* ---- PLANET-IN-HOUSE + ASPECTS side by side ---- */}
      <TopGrid style={{ marginTop: 20 }}>
        {result.navamsa && (
          <Card>
            <CardTitle>Planet-in-House Map</CardTitle>
            <SectionDesc>Which planets occupy which houses, and where each house lord is placed.</SectionDesc>
            <Table>
              <thead><tr><Th>House</Th><Th>Sign</Th><Th>Occupants</Th><Th>Lord</Th><Th>Lord in</Th></tr></thead>
              <tbody>
                {(result.house_analysis || []).map(h => (
                  <tr key={h.house} style={h.occupants?.length > 0 ? {fontWeight:600} : {}}>
                    <Td>H{h.house}</Td><Td>{h.sign}</Td>
                    <Td>{h.occupants?.length > 0 ? h.occupants.join(', ') : <span style={{color:'#ccc'}}>—</span>}</Td>
                    <Td>{h.lord}</Td><Td>H{h.lord_house} ({h.lord_sign})</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        )}
        {result.aspects && (
          <Card>
            <CardTitle>Planetary Aspects</CardTitle>
            <SectionDesc>Which houses each planet aspects. Special aspects: Mars (4th,8th), Jupiter (5th,9th), Saturn (3rd,10th), Rahu/Ketu (3rd,11th). All planets have 7th aspect.</SectionDesc>
            <Table>
              <thead><tr><Th>Planet</Th><Th>In</Th><Th>Aspects Houses</Th></tr></thead>
              <tbody>
                {result.aspects.map(a => (
                  <tr key={a.planet}>
                    <Td style={{fontWeight:600}}>{a.planet}{a.is_retrograde ? ' (R)' : ''}</Td>
                    <Td>H{a.from_house} ({a.from_sign})</Td>
                    <Td>{a.aspects.map((h, i) => (
                      <span key={h}>
                        <Badge bg="#7986cb" style={{margin:'0 2px'}}>H{h}</Badge>
                        <span style={{fontSize:'0.6rem',color:'#999'}}>{a.aspect_types[i]}</span>
                        {' '}
                      </span>
                    ))}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        )}
      </TopGrid>

      {/* ---- TRANSIT PANEL + SADE SATI (today mode only) ---- */}
      {result.transits && (
        <TopGrid style={{ marginTop: 20 }}>
          <Card>
            <CardTitle>Current Transits (Gochara)</CardTitle>
            <SectionDesc>Where planets are transiting TODAY relative to your birth chart. BAV score shows how favorable each transit is (0-8, higher=better, 4=neutral).</SectionDesc>
            <Table>
              <thead><tr><Th>Planet</Th><Th>Transit Sign</Th><Th>House</Th><Th>BAV</Th><Th>Nakshatra</Th></tr></thead>
              <tbody>
                {result.transits.map(t => (
                  <tr key={t.planet}>
                    <Td style={{fontWeight:600}}>{t.planet}{t.is_retrograde ? ' (R)' : ''}</Td>
                    <Td>{t.sign}</Td>
                    <Td>H{t.house}</Td>
                    <Td>{t.bav >= 0
                      ? <span style={{fontWeight:600,color: t.bav >= 5 ? '#4caf50' : t.bav >= 3 ? '#ff9800' : '#f44336'}}>{t.bav}/8</span>
                      : <span style={{color:'#ccc'}}>—</span>
                    }</Td>
                    <Td style={{fontSize:'0.78rem'}}>{t.nakshatra}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
          {result.sade_sati && (
            <Card>
              <CardTitle>Sade Sati Status</CardTitle>
              <SectionDesc>Saturn's 7.5-year transit through 12th, 1st, and 2nd houses from Moon. One of the most significant transit periods in Vedic astrology.</SectionDesc>
              <div style={{background: result.sade_sati.active ? 'linear-gradient(135deg,#ffebee,#fce4ec)' : '#f1f8e9', borderRadius:10, padding:16, textAlign:'center'}}>
                <div style={{fontSize:'1.4rem',fontWeight:700,color: result.sade_sati.active ? '#c62828' : '#2e7d32',marginBottom:6}}>
                  {result.sade_sati.active ? `⚠ ${result.sade_sati.label}` : '✓ ' + result.sade_sati.label}
                </div>
                <div style={{fontSize:'0.85rem',color:'#555',lineHeight:1.5}}>{result.sade_sati.detail}</div>
              </div>
            </Card>
          )}
        </TopGrid>
      )}

      {/* ---- NAKSHATRA TABLE + COMBUSTION ---- */}
      <TopGrid style={{ marginTop: 20 }}>
        {result.nakshatra_table && (
          <Card>
            <CardTitle>Nakshatra & Pada</CardTitle>
            <SectionDesc>Each planet's star (nakshatra) and quarter (pada). The nakshatra lord drives Dasha timing — always cross-reference with the Dasha tree.</SectionDesc>
            <Table>
              <thead><tr><Th>Planet</Th><Th>Nakshatra</Th><Th>Pada</Th><Th>Nak Lord</Th><Th>Sign</Th><Th>Degree</Th></tr></thead>
              <tbody>
                {result.nakshatra_table.map(n => (
                  <tr key={n.planet}>
                    <Td style={{fontWeight:600}}>{n.planet}{n.is_retrograde ? ' (R)' : ''}</Td>
                    <Td>{n.nakshatra}</Td>
                    <Td>P{n.pada}</Td>
                    <Td style={{fontWeight:600}}>{n.nak_lord}</Td>
                    <Td>{n.sign_name}</Td>
                    <Td>{n.degree}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        )}
        {result.combustion && (
          <Card>
            <CardTitle>Combustion Check</CardTitle>
            <SectionDesc>Planets too close to the Sun lose their significations. A combust planet can't deliver its full potential — its house lordship and karakatva are weakened.</SectionDesc>
            <Table>
              <thead><tr><Th>Planet</Th><Th>Distance</Th><Th>Orb</Th><Th>Status</Th></tr></thead>
              <tbody>
                {result.combustion.map(c => (
                  <tr key={c.planet} style={{background: c.is_combust ? '#fff3e0' : 'transparent'}}>
                    <Td style={{fontWeight:600}}>{c.planet}</Td>
                    <Td>{c.distance.toFixed(1)}°</Td>
                    <Td>{c.orb}°</Td>
                    <Td style={{fontWeight:600,color: c.is_combust ? '#e65100' : '#4caf50'}}>
                      {c.is_combust ? 'COMBUST' : 'Clear'}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        )}
      </TopGrid>
    </>)}

    {/* ---- VIMSHOTTARI DASHA (full, like FreeKundli) ---- */}
    {result?.dasha && <KSDashaSection dasha={result.dasha} dashaActivation={result.dasha_activation} ks={ks} houseAnalysis={result.house_analysis} />}

    {/* ---- DASHA INTERPRETATION NARRATIVE ---- */}
    {result?.dasha_interpretation && <KSDashaInterpretation interp={result.dasha_interpretation} lang={interpLang} onLangChange={setInterpLang} />}

    {/* ---- LIFE PERIOD FORECAST ---- */}
    <LifeForecastPanel result={result} expandedHouse={expandedHouse} setExpandedHouse={setExpandedHouse} birthPayload={birthPayload} />

    <Card style={{ marginTop: 20 }}>
      <CardTitle>12 House Scores</CardTitle>
      <Desc>Each house represents a life area. The score combines house strength (HPS), yogas/doshas, varga confirmation, and temporal activation. Click a house to see the full breakdown.</Desc>
      <HouseGrid>
        {ks.houses.map(h => (
          <HouseCard key={h.house} expanded={expandedHouse === h.house} onClick={() => setExpandedHouse(expandedHouse === h.house ? null : h.house)}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <HouseNum>H{h.house}</HouseNum>
              <Badge bg={h.color || '#ccc'}>{h.score_out_100}</Badge>
            </div>
            <HouseInfo>{h.sign_name} | {h.lord}</HouseInfo>
            <HouseInfo>{h.life_area}</HouseInfo>
            <ScoreBar><ScoreFill color={h.color || '#ccc'} pct={h.score_out_100} /></ScoreBar>
            {expandedHouse === h.house && (
              <DrillDown>
                {h.narrative && <div style={{ fontSize: '0.8rem', color: '#555', fontStyle: 'italic', marginBottom: 8, lineHeight: 1.4 }}>{h.narrative}</div>}
                <DrillRow title="House Potency Score: Bhava Bala 30% + Lord PSS 25% + SAV 15% + Occupants 15% + Aspects 10% + Panchanga 5%"><span>HPS (Base)</span><span>{h.hps?.toFixed(3)}</span></DrillRow>
                <DrillRow title="Product of all yoga multipliers (>1 = boost) and dosha multipliers (<1 = penalty) affecting this house"><span>Yoga/Dosha Mod</span><span>&times;{h.yoga_dosha_modifier?.toFixed(3)}</span></DrillRow>
                <DrillRow title="Varga confirmation: karaka planet's dignity in the house's key divisional chart (±15%)"><span>Varga Enhance</span><span>&times;{h.varga_enhancement?.toFixed(3)}</span></DrillRow>
                <DrillRow title="HPS × Yoga/Dosha × Varga — your permanent life potential for this area"><span>Life Potential</span><span>{h.life_potential?.toFixed(3)}</span></DrillRow>
                <DrillRow title="Transit activation: DRS 30% + TRS 30% + Panchanga 15% + Tara 10% + Chandra 10% + Sudarshana 5% (1.0 in static mode)"><span>Temporal</span><span>&times;{h.temporal_activation?.toFixed(3)}</span></DrillRow>
                {(h.drs > 0 || h.trs > 0) && (
                  <>
                    <DrillRow title="Dasha Relevance Score: how strongly the 5-level dasha chain (MD→AD→PD→SK→PR) activates this house through lordship, occupation, aspects, dispositor, and star lord connections"><span style={{paddingLeft:12,fontSize:'0.75rem',color:'#7b1fa2'}}>↳ DRS</span><span style={{color:'#7b1fa2'}}>{h.drs?.toFixed(3)}</span></DrillRow>
                    <DrillRow title="Transit Relevance Score: Ashtakavarga-weighted transit strength — how supportive current transits are for this house"><span style={{paddingLeft:12,fontSize:'0.75rem',color:'#1565c0'}}>↳ TRS</span><span style={{color:'#1565c0'}}>{h.trs?.toFixed(3)}</span></DrillRow>
                  </>
                )}
                <DrillRow title="Hourly precision: Kakshya (70%) + Hora lord (30%), with Rahu Kalam penalty. Only active when a specific hour is selected."><span>Sub-Daily</span><span>&times;{h.sub_daily_factor?.toFixed(3)}</span></DrillRow>
                <DrillRow title="Double Transit bonus: Jupiter + Saturn both aspecting this house from transit"><span>DT Bonus</span><span>+{(h.double_transit_bonus * 100)?.toFixed(1)}%</span></DrillRow>
                <DrillRow style={{ fontWeight: 700 }} title="Life Potential × Temporal × Sub-Daily × (1 + DT Bonus)"><span>Final</span><span>{h.final_score?.toFixed(3)}</span></DrillRow>
              </DrillDown>
            )}
          </HouseCard>
        ))}
      </HouseGrid>
    </Card>
    <TopGrid style={{ marginTop: 20 }}>
      <Card>
        <CardTitle>Planet Strength (PSS)</CardTitle>
        <SectionDesc>PSS = Weighted Sum × Baladi Gate. The weighted sum combines Shadbala (35%), Vimshopaka (20%), BAV (15%), Ishta (10%), Avastha Effectiveness (10%), and Residential (10%). The Baladi gate (Yuva=1.0×, Kumara=0.5×, Bala=0.25×, Vridha=0.125×, Mrita=0.0625×) is the dominant differentiator — a planet in "dead" state retains only 6% of its potential. Click a row to see the full breakdown.</SectionDesc>
        <Table>
          <thead><tr><Th>Planet</Th><Th>Baladi</Th><Th>Gate</Th><Th>W.Sum</Th><Th>PSS</Th><Th style={{fontSize:'0.7rem'}}>Shadb.</Th><Th style={{fontSize:'0.7rem'}}>Vimsh.</Th><Th style={{fontSize:'0.7rem'}}>BAV</Th><Th style={{fontSize:'0.7rem'}}>Ishta</Th><Th style={{fontSize:'0.7rem'}}>Avast.</Th><Th style={{fontSize:'0.7rem'}}>Resid.</Th></tr></thead>
          <tbody>
            {ks.pss?.map(p => (
              <React.Fragment key={p.planet}>
              <tr onClick={() => setExpandedHouse(expandedHouse === 'pss_'+p.planet ? null : 'pss_'+p.planet)} style={{cursor:'pointer'}}>
                <Td style={{ fontWeight: 600 }}>{p.planet}{(() => { const cp = result?.chart?.planets?.find(x => x.planet === p.planet); return cp?.is_retrograde ? ' (R)' : ''; })()}</Td>
                <Td><span style={{fontSize:'0.7rem',padding:'1px 5px',borderRadius:8,background: p.avastha_multiplier >= 1 ? '#e8f5e9' : p.avastha_multiplier >= 0.5 ? '#fff8e1' : p.avastha_multiplier >= 0.125 ? '#fff3e0' : '#fde8e8', color: p.avastha_multiplier >= 1 ? '#2e7d32' : p.avastha_multiplier >= 0.5 ? '#f57f17' : '#c62828'}}>{p.baladi_state || '?'}</span></Td>
                <Td style={{fontWeight:600, color: p.avastha_multiplier >= 1 ? '#4caf50' : p.avastha_multiplier >= 0.5 ? '#ff9800' : '#f44336'}}>{p.avastha_multiplier?.toFixed(3)}</Td>
                <Td>{p.weighted_sum?.toFixed(3)}</Td>
                <Td style={{ fontWeight: 700, color: p.pss >= 0.5 ? '#4caf50' : p.pss >= 0.2 ? '#ff9800' : '#f44336' }}>{p.pss?.toFixed(3)}</Td>
                <Td style={{fontSize:'0.75rem'}}>{p.shadbala_ratio?.toFixed(2)}</Td>
                <Td style={{fontSize:'0.75rem'}}>{p.vimshopaka_norm?.toFixed(2)}</Td>
                <Td style={{fontSize:'0.75rem'}}>{p.bav_avg?.toFixed(2)}</Td>
                <Td style={{fontSize:'0.75rem'}}>{p.ishta_ratio?.toFixed(2)}</Td>
                <Td style={{fontSize:'0.75rem'}}>{p.avastha_effectiveness?.toFixed(2)}</Td>
                <Td style={{fontSize:'0.75rem'}}>{p.residential?.toFixed(2)}</Td>
              </tr>
              {expandedHouse === 'pss_'+p.planet && (
                <tr><td colSpan={11} style={{padding:'8px 12px',background:'#fafafa',fontSize:'0.78rem'}}>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'4px 20px',maxWidth:500}}>
                    <span>Shadbala: 0.35 × {p.shadbala_ratio?.toFixed(3)}</span><span style={{textAlign:'right'}}>= {(0.35 * (p.shadbala_ratio||0)).toFixed(4)}</span>
                    <span>Vimshopaka: 0.20 × {p.vimshopaka_norm?.toFixed(3)}</span><span style={{textAlign:'right'}}>= {(0.20 * (p.vimshopaka_norm||0)).toFixed(4)}</span>
                    <span>BAV: 0.15 × {p.bav_avg?.toFixed(3)}</span><span style={{textAlign:'right'}}>= {(0.15 * (p.bav_avg||0)).toFixed(4)}</span>
                    <span>Ishta: 0.10 × {p.ishta_ratio?.toFixed(3)}</span><span style={{textAlign:'right'}}>= {(0.10 * (p.ishta_ratio||0)).toFixed(4)}</span>
                    <span>Avastha Eff: 0.10 × {p.avastha_effectiveness?.toFixed(3)}</span><span style={{textAlign:'right'}}>= {(0.10 * (p.avastha_effectiveness||0)).toFixed(4)}</span>
                    <span>Residential: 0.10 × {p.residential?.toFixed(3)}</span><span style={{textAlign:'right'}}>= {(0.10 * (p.residential||0)).toFixed(4)}</span>
                    <span style={{fontWeight:600,borderTop:'1px solid #ddd',paddingTop:4}}>Weighted Sum</span><span style={{fontWeight:600,textAlign:'right',borderTop:'1px solid #ddd',paddingTop:4}}>= {p.weighted_sum?.toFixed(4)}</span>
                    <span style={{fontWeight:600}}>× Baladi Gate ({p.baladi_state})</span><span style={{fontWeight:600,textAlign:'right'}}>× {p.avastha_multiplier?.toFixed(4)}</span>
                    <span style={{fontWeight:700,color:'#e91e63'}}>Final PSS</span><span style={{fontWeight:700,textAlign:'right',color:'#e91e63'}}>= {p.pss?.toFixed(4)}</span>
                  </div>
                  <div style={{marginTop:6,fontSize:'0.72rem',color:'#888'}}>
                    Avastha: {p.baladi_state} (Baladi) + {p.jagradadi_state} (Jagradadi) + {p.deeptadi_state} (Deeptadi). Effectiveness = avg of Jagradadi + Deeptadi scores (Baladi excluded to avoid double-counting with the gate).
                  </div>
                </td></tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
        {/* Planet Strength Bar */}
        {ks.pss && (
          <div style={{marginTop:16}}>
            <div style={{fontSize:'0.78rem',fontWeight:600,color:'#555',marginBottom:6}}>Planet Power Distribution</div>
            {[...ks.pss].sort((a,b) => b.pss - a.pss).map(p => {
              const pct = Math.min(p.pss / 0.6 * 100, 100);
              const cp = result?.chart?.planets?.find(x => x.planet === p.planet);
              return (
                <div key={p.planet} style={{display:'flex',alignItems:'center',gap:6,marginBottom:3,fontSize:'0.75rem'}}>
                  <span style={{width:60,fontWeight:600,textAlign:'right'}}>{p.planet}{cp?.is_retrograde ? '(R)' : ''}</span>
                  <div style={{flex:1,height:12,background:'#f5f5f5',borderRadius:3,overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${pct}%`,background: p.pss >= 0.5 ? '#4caf50' : p.pss >= 0.2 ? '#ff9800' : '#f44336',borderRadius:3,transition:'width 0.3s'}} />
                  </div>
                  <span style={{width:40,color:'#888'}}>{p.pss?.toFixed(3)}</span>
                  <span style={{width:75,fontSize:'0.65rem',color:'#aaa'}}>{cp?.nakshatra?.slice(0,8)} P{cp?.pada}</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>
      <Card>
        <CardTitle>Yoga & Dosha Analysis</CardTitle>
        <SectionDesc>Score (0-100) = Planet Strength (40) + Dignity (30) + House Placement (20) - Afflictions (10). The effective multiplier shows the actual boost/penalty applied to affected houses, scaled by involved planets' PSS. 20 classical yoga patterns and 7 dosha patterns are evaluated.</SectionDesc>
      </Card>
    </TopGrid>

    {/* ---- UNIFIED YOGA & DOSHA SECTION ---- */}
    <Card style={{ marginTop: 20 }}>
      {/* YOGAS */}
      {result?.yoga_analysis?.length > 0 && (
        <>
          <div style={{fontSize:'0.85rem',fontWeight:600,color:'#4caf50',marginBottom:8}}>Active Yogas ({result.yoga_analysis.length})</div>
          {result.yoga_analysis.map((y, i) => {
            const scoreColor = y.manifestation_score >= 80 ? '#4caf50' : y.manifestation_score >= 60 ? '#8bc34a' : y.manifestation_score >= 40 ? '#ff9800' : '#f44336';
            const levelIcon = {triple:'💎',full:'🟢',primary:'🟡',secondary:'🟠',dormant:'⚪'}[y.activation_level] || '?';
            const isExp = expandedHouse === 'yoga_'+i;
            return (
              <div key={i} onClick={() => setExpandedHouse(isExp ? null : 'yoga_'+i)}
                style={{cursor:'pointer',padding:'10px 14px',marginBottom:8,borderRadius:8,
                  border:`1px solid ${isExp ? scoreColor : '#eee'}`,
                  background: isExp ? '#fafafa' : 'transparent',transition:'all 0.2s'}}>
                {/* Row 1: Name + Score */}
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <span style={{fontSize:'1.1rem'}}>{levelIcon}</span>
                    <span style={{fontWeight:700}}>{y.name}</span>
                    {y.category && <span style={{fontSize:'0.65rem',background:'#f0f0f0',padding:'1px 6px',borderRadius:4,color:'#888'}}>{y.category}</span>}
                    {y.effective_multiplier > 0 && <Badge bg={scoreColor} style={{fontSize:'0.6rem'}}>&times;{y.effective_multiplier?.toFixed(2)}</Badge>}
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{textAlign:'right',lineHeight:1.2}}>
                      <div style={{fontSize:'0.65rem',color:'#888'}}>Birth</div>
                      <div style={{fontWeight:700,color:scoreColor,fontSize:'0.8rem'}}>{y.birth_potential || y.manifestation_score}</div>
                    </div>
                    <div style={{width:1,height:24,background:'#ddd'}} />
                    <div style={{textAlign:'right',lineHeight:1.2}}>
                      <div style={{fontSize:'0.65rem',color:'#888'}}>Now</div>
                      <div style={{fontWeight:700,color: (y.current_activation||0) > 20 ? '#4caf50' : (y.current_activation||0) > 5 ? '#ff9800' : '#ccc',fontSize:'0.8rem'}}>{y.current_activation ?? 0}</div>
                    </div>
                    <span style={{fontSize:'0.6rem',color:'#aaa'}}>/100</span>
                  </div>
                </div>
                {/* Row 2: Description */}
                <div style={{fontSize:'0.78rem',color:'#666',marginTop:4}}>{y.description?.slice(0,150)}{y.description?.length > 150 ? '...' : ''}</div>
                {/* Row 3: Life Areas (the key addition) */}
                {y.affected_life_areas?.length > 0 && (
                  <div style={{marginTop:5}}>
                    <span style={{fontSize:'0.72rem',color:'#888'}}>Impact: </span>
                    {y.affected_life_areas.map((area, ai) => (
                      <span key={ai} style={{fontSize:'0.72rem',background:'#e8f5e9',color:'#2e7d32',padding:'1px 6px',borderRadius:4,marginRight:4,fontWeight:600}}>{area}</span>
                    ))}
                  </div>
                )}
                {/* Row 4: Radar impact chips */}
                {y.radar_impact?.length > 0 && (
                  <div style={{display:'flex',gap:6,marginTop:4,flexWrap:'wrap'}}>
                    {y.radar_impact.map((r, ri) => (
                      <span key={ri} style={{fontSize:'0.68rem',padding:'1px 6px',borderRadius:4,fontWeight:600,
                        background: r.impact > 0 ? '#e8f5e9' : '#ffebee',
                        color: r.impact > 0 ? '#2e7d32' : '#c62828'}}>
                        {r.dimension}: {r.impact > 0 ? '+' : ''}{r.impact.toFixed(1)} pts
                      </span>
                    ))}
                  </div>
                )}
                {/* Row 5: Planets + Houses + Multiplier */}
                <div style={{display:'flex',gap:12,marginTop:4,fontSize:'0.75rem',flexWrap:'wrap'}}>
                  <span style={{color:'#555'}}>Planets: <strong>{y.planets?.join(', ')}</strong></span>
                  {y.affected_houses?.length > 0 && <span style={{color:'#555'}}>Houses: <strong>H{y.affected_houses.join(', H')}</strong></span>}
                </div>
                {/* Row 6: Activation status */}
                <div style={{fontSize:'0.78rem',marginTop:4}}>
                  <span style={{color: y.currently_active ? '#4caf50' : '#999',fontWeight:600}}>{y.current_status}</span>
                </div>
                {y.next_activation && !y.activation_windows?.some(w => w.is_current && (w.level === 'full' || w.level === 'triple')) && (
                  <div style={{fontSize:'0.75rem',color:'#1565c0',marginTop:3}}>
                    Next {y.next_activation.level}: <strong>{y.next_activation.period}</strong> ({y.next_activation.start?.slice(0,4)}–{y.next_activation.end?.slice(0,4)})
                    {y.next_activation.peak_month && <span style={{color:'#7b1fa2'}}> — Peak: {y.next_activation.peak_month.period}</span>}
                  </div>
                )}
                {/* Expanded: activation timeline */}
                {isExp && y.activation_windows?.length > 0 && (
                  <div style={{marginTop:10,paddingTop:8,borderTop:'1px solid #eee'}}>
                    <div style={{fontSize:'0.75rem',fontWeight:600,color:'#555',marginBottom:6}}>Activation Windows ({y.activation_windows.length} periods)</div>
                    <div style={{maxHeight:200,overflowY:'auto'}}>
                      {y.activation_windows.filter(w => w.level === 'full' || w.level === 'triple' || w.is_current).slice(0,15).map((w,j) => {
                        const wColor = {triple:'#7b1fa2',full:'#4caf50',primary:'#ff9800',secondary:'#90a4ae'}[w.level] || '#ccc';
                        return (
                          <div key={j} style={{display:'flex',alignItems:'center',gap:8,padding:'3px 0',fontSize:'0.72rem',
                            fontWeight: w.is_current ? 700 : 400,color: w.is_current ? '#e91e63' : '#555'}}>
                            <span style={{width:8,height:8,borderRadius:'50%',background:wColor,flexShrink:0}} />
                            <span style={{minWidth:90}}>{w.period}</span>
                            <span style={{color:'#999'}}>{w.start?.slice(0,4)}–{w.end?.slice(0,4)}</span>
                            <Badge bg={wColor} style={{fontSize:'0.6rem'}}>{w.level}</Badge>
                            {w.is_current && <span style={{color:'#e91e63',fontWeight:700}}>NOW</span>}
                            {w.peak_month && <span style={{color:'#7b1fa2',fontSize:'0.65rem'}}>Peak: {w.peak_month.period} ({w.peak_month.start?.slice(0,7)})</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {/* DOSHAS */}
      {result?.dosha_analysis?.length > 0 && (
        <>
          <div style={{fontSize:'0.85rem',fontWeight:600,color:'#f44336',marginTop:16,marginBottom:8}}>Active Doshas ({result.dosha_analysis.length})</div>
          {result.dosha_analysis.map((d, i) => {
            const sevColor = d.severity === 'high' ? '#c62828' : d.severity === 'medium' ? '#e65100' : '#888';
            const sevBg = d.severity === 'high' ? '#ffebee' : d.severity === 'medium' ? '#fff3e0' : '#f5f5f5';
            return (
              <div key={i} style={{padding:'10px 14px',marginBottom:8,borderRadius:8,border:`1px solid ${sevColor}22`,background:sevBg}}>
                {/* Row 1: Name + Severity + Multiplier */}
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <span style={{fontSize:'1rem'}}>⚠</span>
                    <span style={{fontWeight:700,color:sevColor}}>{d.name}</span>
                    <span style={{fontSize:'0.65rem',background:sevColor+'22',color:sevColor,padding:'1px 6px',borderRadius:4}}>{d.severity}</span>
                  </div>
                  {d.effective_multiplier > 0 && <Badge bg={sevColor} style={{fontSize:'0.7rem'}}>&times;{d.effective_multiplier?.toFixed(2)}</Badge>}
                </div>
                {/* Row 2: Description */}
                <div style={{fontSize:'0.78rem',color:'#666',marginTop:4}}>{d.description}</div>
                {/* Row 3: Life Areas */}
                {d.affected_life_areas?.length > 0 && (
                  <div style={{marginTop:5}}>
                    <span style={{fontSize:'0.72rem',color:'#888'}}>Impact: </span>
                    {d.affected_life_areas.map((area, ai) => (
                      <span key={ai} style={{fontSize:'0.72rem',background:'#ffebee',color:'#c62828',padding:'1px 6px',borderRadius:4,marginRight:4,fontWeight:600}}>{area}</span>
                    ))}
                  </div>
                )}
                {/* Row 4: Radar impact */}
                {d.radar_impact?.length > 0 && (
                  <div style={{display:'flex',gap:6,marginTop:4,flexWrap:'wrap'}}>
                    {d.radar_impact.map((r, ri) => (
                      <span key={ri} style={{fontSize:'0.68rem',padding:'1px 6px',borderRadius:4,fontWeight:600,background:'#ffebee',color:'#c62828'}}>
                        {r.dimension}: {r.impact.toFixed(1)} pts
                      </span>
                    ))}
                  </div>
                )}
                {/* Row 5: Houses */}
                {d.affected_houses?.length > 0 && (
                  <div style={{fontSize:'0.75rem',color:'#555',marginTop:4}}>Houses: <strong>H{d.affected_houses.join(', H')}</strong></div>
                )}
                {/* Row 6: Cancellations */}
                {d.cancellations?.length > 0 && (
                  <div style={{fontSize:'0.72rem',color:'#2e7d32',marginTop:4}}>
                    Cancellations: {d.cancellations.join('; ')}
                  </div>
                )}
                {/* Row 7: Remedies */}
                {d.remedies?.length > 0 && (
                  <div style={{fontSize:'0.72rem',color:'#1565c0',marginTop:3}}>
                    Remedies: {d.remedies.join('; ')}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {(!result?.yoga_analysis?.length && !result?.dosha_analysis?.length) && (
        <div style={{ color: '#aaa', textAlign: 'center', padding: 20 }}>No significant yogas or doshas detected</div>
      )}
    </Card>

    {ks.temporal_context && (
      <Card style={{ marginTop: 20 }}>
        <CardTitle>Dasha Context</CardTitle>
        <SectionDesc>The Vimshottari Dasha system divides life into planetary periods at 5 levels. The current period lords influence which houses are activated. Panchanga Quality reflects the auspiciousness of the birth muhurta. Sudarshana, Tara Bala, and Chandra Bala are transit-dependent and only computed in dynamic mode.</SectionDesc>
        <Table>
          <tbody>
            <tr><Td style={{fontWeight:600}}>Mahadasha</Td><Td>{ks.temporal_context.dasha_lord}</Td></tr>
            <tr><Td style={{fontWeight:600}}>Antardasha</Td><Td>{ks.temporal_context.antardasha_lord}</Td></tr>
            {ks.temporal_context.pratyant_lord && <tr><Td style={{fontWeight:600}}>Pratyantardasha</Td><Td>{ks.temporal_context.pratyant_lord}</Td></tr>}
            {ks.temporal_context.sukshma_lord && <tr><Td style={{fontWeight:600}}>Sukshma</Td><Td>{ks.temporal_context.sukshma_lord}</Td></tr>}
            {ks.temporal_context.prana_lord && <tr><Td style={{fontWeight:600}}>Prana</Td><Td>{ks.temporal_context.prana_lord}</Td></tr>}
            <tr><Td style={{fontWeight:600}}>Panchanga Quality</Td><Td>{ks.temporal_context.panchanga_quality?.toFixed(3)}</Td></tr>
            <tr><Td style={{fontWeight:600}}>Sudarshana Score</Td><Td>{ks.mode === 'static' || !ks.temporal_context.sudarshana_score ? <span style={{color:'#bbb'}}>N/A (transit-dependent)</span> : ks.temporal_context.sudarshana_score?.toFixed(3)}</Td></tr>
            {ks.temporal_context.tara_bala ? <tr><Td style={{fontWeight:600}}>Tara Bala</Td><Td>{ks.temporal_context.tara_bala.tara_name} ({ks.temporal_context.tara_bala.score?.toFixed(2)})</Td></tr> : ks.mode === 'static' && <tr><Td style={{fontWeight:600}}>Tara Bala</Td><Td><span style={{color:'#bbb'}}>N/A (transit-dependent)</span></Td></tr>}
            {ks.temporal_context.chandra_bala ? <tr><Td style={{fontWeight:600}}>Chandra Bala</Td><Td>House {ks.temporal_context.chandra_bala.house_from_moon} ({ks.temporal_context.chandra_bala.score?.toFixed(2)})</Td></tr> : ks.mode === 'static' && <tr><Td style={{fontWeight:600}}>Chandra Bala</Td><Td><span style={{color:'#bbb'}}>N/A (transit-dependent)</span></Td></tr>}
          </tbody>
        </Table>
      </Card>
    )}
  </>
);

/* --- DRS level colors --- */
const drsLevelColor = (level) => ({hot:'#e91e63',active:'#ff9800',warm:'#ffc107',mild:'#90caf9',cool:'#e0e0e0'}[level] || '#e0e0e0');

/* ========== HEADLINE + RADAR ========== */
const HeadlineCard = ({ ks, label }) => (
  <Card>
    <CardTitle>{label}</CardTitle>
    <RingWrap>
      <Ring color={ks.color || '#999'} pct={ks.headline_score}>
        <RingInner><ScoreNum color={ks.color || '#999'}>{ks.headline_score}</ScoreNum><ScoreBand>{ks.band}</ScoreBand></RingInner>
      </Ring>
    </RingWrap>
    <Desc>{ks.mode === 'static'
      ? 'Composite score from all 12 houses, weighted by life-area importance. This is your permanent birth-chart baseline.'
      : 'Dynamic score reflecting today\'s transit energy relative to your birth-chart baseline. Above your static score = favorable day, below = challenging.'
    }</Desc>
    {/* Energy Flow Sentence (dynamic mode only) */}
    {ks.energy_flow && (
      <div style={{background:'linear-gradient(135deg,#1a237e,#4a148c)',borderRadius:10,padding:'12px 16px',margin:'12px 0',color:'#fff',fontSize:'0.85rem',lineHeight:1.6,textAlign:'center'}}>
        <div style={{fontSize:'0.65rem',color:'#b39ddb',marginBottom:4,fontWeight:600,letterSpacing:1}}>ENERGY FLOW</div>
        {ks.energy_flow}
      </div>
    )}
    <RadarWrap><RadarChart data={ks.radar} /></RadarWrap>
    <Desc>Life Radar maps your chart into five dimensions. Career = H10+H6+H2. Wealth = H2+H11+H9. Love = H7+H5+H1. Health = H1+H6+H8. Wisdom = H5+H9+H4.</Desc>
    {/* House Activation Heatmap (dynamic mode only) */}
    {ks.house_activation && (
      <div style={{marginTop:12}}>
        <div style={{fontSize:'0.72rem',color:'#888',fontWeight:600,marginBottom:6,textAlign:'center'}}>DASHA ACTIVATION MAP</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:4}}>
          {ks.house_activation.map(ha => (
            <div key={ha.house} style={{background:drsLevelColor(ha.level),borderRadius:6,padding:'6px 4px',textAlign:'center',opacity: ha.level === 'cool' ? 0.5 : 1}}>
              <div style={{fontSize:'0.7rem',fontWeight:700,color: ha.level === 'cool' ? '#888' : '#333'}}>H{ha.house}</div>
              <div style={{fontSize:'0.6rem',color: ha.level === 'cool' ? '#aaa' : '#555'}}>{ha.drs.toFixed(2)}</div>
            </div>
          ))}
        </div>
        <div style={{display:'flex',justifyContent:'center',gap:8,marginTop:6,fontSize:'0.6rem',color:'#aaa'}}>
          <span style={{display:'flex',alignItems:'center',gap:2}}><span style={{width:8,height:8,borderRadius:2,background:'#e91e63',display:'inline-block'}}/> Hot</span>
          <span style={{display:'flex',alignItems:'center',gap:2}}><span style={{width:8,height:8,borderRadius:2,background:'#ff9800',display:'inline-block'}}/> Active</span>
          <span style={{display:'flex',alignItems:'center',gap:2}}><span style={{width:8,height:8,borderRadius:2,background:'#ffc107',display:'inline-block'}}/> Warm</span>
          <span style={{display:'flex',alignItems:'center',gap:2}}><span style={{width:8,height:8,borderRadius:2,background:'#90caf9',display:'inline-block'}}/> Mild</span>
          <span style={{display:'flex',alignItems:'center',gap:2}}><span style={{width:8,height:8,borderRadius:2,background:'#e0e0e0',display:'inline-block'}}/> Cool</span>
        </div>
      </div>
    )}
    <div style={{ textAlign: 'center', fontSize: '0.75rem', color: '#aaa', marginTop: 4 }}>{ks.mode === 'static' ? 'Static mode (Stages 1+2+3+5)' : 'Dynamic mode (All 6 stages)'}</div>
  </Card>
);

/* ========== TIMELINE BAR CHART (SVG) ========== */
const TimelineChart = ({ data, selectedHouse }) => {
  if (!data || data.length === 0) return null;
  const W = Math.max(data.length * 28, 600);
  const H = 200;
  const pad = { t: 20, b: 40, l: 30, r: 10 };
  const cw = (W - pad.l - pad.r) / data.length;
  return (
    <ChartWrap>
      <svg width={W} height={H} style={{ display: 'block' }}>
        {/* Y gridlines */}
        {[0, 25, 50, 75, 100].map(v => {
          const y = pad.t + (100 - v) / 100 * (H - pad.t - pad.b);
          return <g key={v}><line x1={pad.l} x2={W - pad.r} y1={y} y2={y} stroke="#eee" /><text x={pad.l - 4} y={y + 3} textAnchor="end" fontSize={9} fill="#aaa">{v}</text></g>;
        })}
        {/* Bars */}
        {data.map((d, i) => {
          const score = selectedHouse > 0 ? (d.houses[selectedHouse - 1]?.score || 0) : d.headline_score;
          const color = (selectedHouse > 0 ? d.houses?.[selectedHouse - 1]?.color : d.color) || '#999';
          const bh = score / 100 * (H - pad.t - pad.b);
          const x = pad.l + i * cw + 2;
          const y = H - pad.b - bh;
          return (
            <g key={i}>
              <rect x={x} y={y} width={cw - 4} height={bh} fill={color} rx={2} opacity={0.85} />
              <text x={x + cw / 2 - 2} y={H - pad.b + 12} textAnchor="middle" fontSize={7} fill="#888" transform={`rotate(-45,${x + cw / 2 - 2},${H - pad.b + 12})`}>{d.date.slice(5)}</text>
              <text x={x + cw / 2 - 2} y={y - 3} textAnchor="middle" fontSize={8} fill="#555" fontWeight={600}>{score}</text>
            </g>
          );
        })}
      </svg>
    </ChartWrap>
  );
};

/* ========== MAIN COMPONENT ========== */
const KalaSakthiDashboard = () => {
  const location = useLocation();
  const [formValues, setFormValues] = useState({
    name: '', gender: 'male', dob: '', hour: '', minute: '', period: 'AM', timeFormat: '24h',
    place: '', latitude: null, longitude: null, timezone: 'Asia/Kolkata', ayanamsa: 'lahiri',
  });
  const [ayanamsas, setAyanamsas] = useState([]);
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [profileResult, setProfileResult] = useState(null);
  const [todayResult, setTodayResult] = useState(null);
  const [timelineData, setTimelineData] = useState(null);
  const [expandedHouse, setExpandedHouse] = useState(null);
  const [interpLang, setInterpLang] = useState('en');
  const [targetDate, setTargetDate] = useState(today());
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10); });
  const [tlHouse, setTlHouse] = useState(0); // 0 = headline

  // Auto-populate initial values from call context (astrologer clicking User Details during call)
  const [autoInitial, setAutoInitial] = useState(null);
  useEffect(() => {
    const ap = location.state?.autoPopulate;
    if (!ap) return;
    setAutoInitial({
      name: ap.name || '', gender: ap.gender || 'male',
      dob: ap.date_of_birth || '',
      hour: parseInt((ap.time_of_birth || '0:0').split(':')[0]) || 0,
      minute: parseInt((ap.time_of_birth || '0:0').split(':')[1]) || 0,
      period: 'AM', timeFormat: '24h',
      place: ap.place_of_birth || '', latitude: ap.latitude || null, longitude: ap.longitude || null,
      timezone: 'Asia/Kolkata', ayanamsa: 'lahiri',
    });
  }, [location.state]);

  useEffect(() => { freeKundliService.listAyanamsas().then(r => { if (r.data?.ayanamsas) setAyanamsas(r.data.ayanamsas); }).catch(() => {}); }, []);

  // Build API payload from form values.
  // When called with values from BirthDataForm's onSubmit, hour is already 24h-converted.
  // When called with raw formValues (e.g. button click), pass raw=false to convert.
  const buildPayload = useCallback((vals, { hourAlready24h = false } = {}) => {
    const dobParts = (vals.dob || '').split('-');
    const hour24 = hourAlready24h
      ? parseInt(vals.hour || '0')
      : to24Hour(parseInt(vals.hour || '0'), vals.period || 'AM', vals.timeFormat || '24h');
    return {
      name: vals.name, gender: vals.gender,
      year: parseInt(dobParts[0]), month: parseInt(dobParts[1]), day: parseInt(dobParts[2]),
      hour: hour24, minute: parseInt(vals.minute || '0'),
      place: vals.place, latitude: parseFloat(vals.latitude), longitude: parseFloat(vals.longitude),
      ayanamsa: vals.ayanamsa, lang: interpLang,
    };
  }, [interpLang]);

  const lastPayloadRef = useRef(null);

  const birthPayload = useCallback(() => buildPayload(formValues, { hourAlready24h: false }), [buildPayload, formValues]);

  const submitProfile = useCallback(async (submitVals) => {
    // submitVals comes from BirthDataForm onSubmit (hour already 24h-converted)
    const vals = submitVals || formValues;
    const hourAlready24h = !!submitVals; // BirthDataForm pre-converts hour
    if (!vals.latitude || !vals.longitude) { setError('Please select a place from the suggestions.'); return; }
    setError(''); setLoading(true);
    try {
      const payload = buildPayload(vals, { hourAlready24h });
      lastPayloadRef.current = payload;
      const r = await kalaSakthiService.computeV2(payload);
      setProfileResult(r.data);
    } catch (err) { setError(err.response?.data?.error || 'Computation failed'); } finally { setLoading(false); }
  }, [formValues, buildPayload]);

  // Re-fetch interpretation for a specific MD-AD (click-to-explore)
  const reloadInterpretation = async (mdLord, adLord) => {
    try {
      const payload = { ...birthPayload(), override_md: mdLord, override_ad: adLord };
      const r = await kalaSakthiService.computeV2(payload);
      setProfileResult(prev => ({ ...prev, dasha_interpretation: r.data.dasha_interpretation }));
    } catch (err) { console.error('Failed to reload interpretation:', err); }
  };

  // Re-fetch when interpretation language changes (only if we already have results)
  useEffect(() => {
    if (profileResult && lastPayloadRef.current) { submitProfile(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interpLang]);

  const submitToday = async (submitValsOrEvent) => {
    if (submitValsOrEvent?.preventDefault) submitValsOrEvent.preventDefault();
    const isFromForm = submitValsOrEvent && !submitValsOrEvent.preventDefault && !submitValsOrEvent.nativeEvent;
    const vals = isFromForm ? submitValsOrEvent : formValues;
    const hourAlready24h = isFromForm;
    if (!vals.latitude || !vals.longitude) { setError('Please select a place from the suggestions.'); return; }
    setError(''); setLoading(true);
    try { const r = await kalaSakthiService.computeToday({ ...buildPayload(vals, { hourAlready24h }), target_date: targetDate }); setTodayResult(r.data); } catch (err) { setError(err.response?.data?.error || 'Computation failed'); } finally { setLoading(false); }
  };

  const submitTimeline = async (submitValsOrEvent) => {
    if (submitValsOrEvent?.preventDefault) submitValsOrEvent.preventDefault();
    const isFromForm = submitValsOrEvent && !submitValsOrEvent.preventDefault && !submitValsOrEvent.nativeEvent;
    const vals = isFromForm ? submitValsOrEvent : formValues;
    const hourAlready24h = isFromForm;
    if (!vals.latitude || !vals.longitude) { setError('Please select a place from the suggestions.'); return; }
    setError(''); setLoading(true);
    try { const r = await kalaSakthiService.computeTimeline({ ...buildPayload(vals, { hourAlready24h }), from_date: fromDate, to_date: toDate }); setTimelineData(r.data); } catch (err) { setError(err.response?.data?.error || 'Computation failed'); } finally { setLoading(false); }
  };

  const profileKs = profileResult?.kalasakthi;
  const todayKs = todayResult?.kalasakthi;

  return (
    <Page>
      <Title>KalaSakthi V2</Title>
      <Sub>Unified Life Blueprint — 6-Stage Vedic Scoring Engine</Sub>

      <TabRow>
        <Tab active={activeTab === 'profile'} onClick={() => setActiveTab('profile')}>My Life Profile</Tab>
        <Tab active={activeTab === 'today'} onClick={() => setActiveTab('today')}>KalaSakthi Today</Tab>
        <Tab active={activeTab === 'timeline'} onClick={() => setActiveTab('timeline')}>Timeline</Tab>
      </TabRow>

      {/* ========== VIEW 1: PROFILE (Static) ========== */}
      {activeTab === 'profile' && (
        <>
          <TopGrid>
            <Card>
              <CardTitle>Birth Details</CardTitle>
              <BirthDataForm
                fields={['name', 'gender', 'dob', 'time', 'place', 'ayanamsa']}
                values={formValues}
                onChange={setFormValues}
                onSubmit={submitProfile}
                submitLabel={loading ? 'Computing...' : 'Compute Life Profile'}
                loading={loading}
                error={error}
                ayanamsaOptions={ayanamsas.length > 0 ? ayanamsas.map(a => a.mode) : ['lahiri']}
                initialValues={autoInitial}
                autoSubmit={!!autoInitial?.latitude}
                pageName="kalaSakthi"
              />
            </Card>
            {loading && !profileKs ? (
              <Card><LoadingState loading={loading} variant="chart" /></Card>
            ) : profileKs ? <HeadlineCard ks={profileKs} label="Life Blueprint" /> : (
              <Card style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa' }}>
                <div style={{ textAlign: 'center' }}><div style={{ fontSize: '3rem', marginBottom: 10 }}>🔱</div><div>Enter birth details to see your permanent life blueprint</div><div style={{fontSize:'0.8rem',marginTop:6,color:'#bbb'}}>Stages 1+2+3+5 (static)</div></div>
              </Card>
            )}
          </TopGrid>
          {profileKs && <HouseResults ks={profileKs} result={profileResult} expandedHouse={expandedHouse} setExpandedHouse={setExpandedHouse} interpLang={interpLang} setInterpLang={setInterpLang} reloadInterpretation={reloadInterpretation} birthPayload={birthPayload} />}
        </>
      )}

      {/* ========== VIEW 2: TODAY (Dynamic) ========== */}
      {activeTab === 'today' && (
        <>
          <TopGrid>
            <Card>
              <CardTitle>Birth Details</CardTitle>
              <BirthDataForm
                fields={['name', 'gender', 'dob', 'time', 'place', 'ayanamsa']}
                values={formValues}
                onChange={setFormValues}
                onSubmit={submitToday}
                loading={loading}
                ayanamsaOptions={ayanamsas.length > 0 ? ayanamsas.map(a => a.mode) : ['lahiri']}
                showSubmit={false}
                pageName="kalaSakthi"
              />
              <FormGroup><Label>Target Date</Label><Input type="date" value={targetDate} onChange={e => setTargetDate(e.target.value)} /></FormGroup>
              <Btn disabled={loading} onClick={submitToday}>{loading ? 'Computing...' : "Compute Today's Score"}</Btn>
              <ErrorAlert message={error} onDismiss={() => setError('')} />
            </Card>
            {loading && !todayKs ? (
              <Card><LoadingState loading={loading} variant="chart" /></Card>
            ) : todayKs ? <HeadlineCard ks={todayKs} label={`KalaSakthi — ${todayResult?.target_date || 'Today'}`} /> : (
              <Card style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa' }}>
                <div style={{ textAlign: 'center' }}><div style={{ fontSize: '3rem', marginBottom: 10 }}>⚡</div><div>See how the stars align for you today</div><div style={{fontSize:'0.8rem',marginTop:6,color:'#bbb'}}>All 6 stages (dynamic)</div></div>
              </Card>
            )}
          </TopGrid>
          {todayKs && <HouseResults ks={todayKs} result={todayResult} expandedHouse={expandedHouse} setExpandedHouse={setExpandedHouse} interpLang={interpLang} setInterpLang={setInterpLang} />}
        </>
      )}

      {/* ========== VIEW 3: TIMELINE ========== */}
      {activeTab === 'timeline' && (
        <>
          <TopGrid>
            <Card>
              <CardTitle>Birth Details</CardTitle>
              <BirthDataForm
                fields={['name', 'gender', 'dob', 'time', 'place', 'ayanamsa']}
                values={formValues}
                onChange={setFormValues}
                onSubmit={submitTimeline}
                loading={loading}
                ayanamsaOptions={ayanamsas.length > 0 ? ayanamsas.map(a => a.mode) : ['lahiri']}
                showSubmit={false}
                pageName="kalaSakthi"
              />
              <Row>
                <FormGroup><Label>From Date</Label><Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} required /></FormGroup>
                <FormGroup><Label>To Date</Label><Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} required /></FormGroup>
              </Row>
              <Btn disabled={loading} onClick={submitTimeline}>{loading ? 'Computing...' : 'Compute Timeline'}</Btn>
              <ErrorAlert message={error} onDismiss={() => setError('')} />
            </Card>
            <Card>
              <CardTitle>Timeline View</CardTitle>
              {timelineData ? (
                <>
                  <div style={{ textAlign: 'center', fontSize: '0.85rem', color: '#666', marginBottom: 12 }}>
                    {timelineData.from_date} to {timelineData.to_date} ({timelineData.days} days)
                  </div>
                  <FormGroup>
                    <Label>Show House</Label>
                    <Select value={tlHouse} onChange={e => setTlHouse(parseInt(e.target.value))}>
                      <option value={0}>Headline Score</option>
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(h => <option key={h} value={h}>H{h}</option>)}
                    </Select>
                  </FormGroup>
                  <TimelineChart data={timelineData.timeline} selectedHouse={tlHouse} />
                  {/* Best / worst days */}
                  {timelineData.timeline?.length > 0 && (() => {
                    const sorted = [...timelineData.timeline].sort((a, b) => b.headline_score - a.headline_score);
                    return (
                      <div style={{ marginTop: 16, fontSize: '0.85rem' }}>
                        <div style={{ color: '#4caf50', fontWeight: 600 }}>Best days: {sorted.slice(0, 3).map(d => `${d.date} (${d.headline_score})`).join(', ')}</div>
                        <div style={{ color: '#f44336', fontWeight: 600, marginTop: 4 }}>Challenging days: {sorted.slice(-3).reverse().map(d => `${d.date} (${d.headline_score})`).join(', ')}</div>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div style={{ textAlign: 'center', color: '#aaa', padding: 30 }}><div style={{ fontSize: '3rem', marginBottom: 10 }}>📅</div><div>Select a date range to see score trends</div><div style={{fontSize:'0.8rem',marginTop:6,color:'#bbb'}}>Drag to compare dates, find best periods</div></div>
              )}
            </Card>
          </TopGrid>
        </>
      )}
    </Page>
  );
};

export default KalaSakthiDashboard;
